import React, { useState } from "react";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import {
  AlertTriangle,
  IconClose,
  IconPlus
} from "@velaro/velaro-component-lib";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton
} from "@/components/ds";
import { Edit as EditIcon, X } from "@/components/ds/icons";

interface HubSpotConfig {
  id: number;
  siteId: number;
  displayName: string;
  hubId: string;
  portalName: string;
  pushTranscripts: boolean;
  createContactIfMissing: boolean;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked?: string;
  authMethod?: string;
  oauthConnectedAt?: string;
}

interface Props {
  configs: HubSpotConfig[];
  onDelete(id: number): void;
  onRefresh(): void;
}

export default function HubSpotAccounts({
  configs,
  onDelete,
  onRefresh
}: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const [deleteTarget, setDeleteTarget] = useState<HubSpotConfig | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await api.admin.delete(`HubSpot/Configs/${deleteTarget.id}`);
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function saveDisplayName(config: HubSpotConfig) {
    setSaving(true);
    try {
      await api.admin.put(`HubSpot/Configs/${config.id}`, {
        displayName: editName
      });
      onRefresh();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(config: HubSpotConfig) {
    await api.admin.put(`HubSpot/Configs/${config.id}`, {
      enabled: !config.enabled
    });
    onRefresh();
  }

  return (
    <SectionCard
      title="Accounts"
      action={
        <PrimaryButton
          label="Add Account"
          onClick={() => navigate("/integrations/hubspot/create")}
          prefixIcon={React.createElement(IconPlus)}
          className="font-medium"
        />
      }
    >
      {configs.length === 0 ? (
        <div className="py-10 flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path
                d="M22.449 12.416c0-.665-.06-1.305-.17-1.921H12v3.633h5.87a5.017 5.017 0 0 1-2.177 3.291v2.73h3.525c2.062-1.9 3.231-4.7 3.231-7.733z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.943 0 5.408-1.005 7.211-2.72l-3.525-2.73c-.976.654-2.225 1.04-3.686 1.04-2.837 0-5.24-1.915-6.1-4.49H2.258v2.82A10.994 10.994 0 0 0 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.9 14.1A6.608 6.608 0 0 1 5.55 12c0-.736.126-1.452.35-2.1V7.08H2.258A10.994 10.994 0 0 0 1 12c0 1.773.425 3.45 1.258 4.92L5.9 14.1z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.41c1.598 0 3.032.549 4.16 1.627l3.12-3.12C17.4 2.145 14.938 1 12 1A10.994 10.994 0 0 0 2.258 7.08L5.9 9.9C6.76 7.325 9.163 5.41 12 5.41z"
                fill="#EA4335"
              />
            </svg>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-zinc-700 mb-1">
              No HubSpot portals connected
            </div>
            <div className="text-xs text-zinc-400 mb-4">
              Connect via OAuth — no token to copy, auto-refreshes forever.
            </div>
            <PrimaryButton
              onClick={() => navigate("/integrations/hubspot/create")}
            >
              Connect HubSpot →
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 text-xs border-b">
              <th className="pb-2 font-medium">Portal</th>
              <th className="pb-2 font-medium">Hub ID</th>
              <th className="pb-2 font-medium">Transcripts</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 w-px whitespace-nowrap"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {configs.map((config) => (
              <tr key={config.id} className="py-2">
                <td className="py-3 pr-4">
                  {editingId === config.id ? (
                    <div className="flex gap-2">
                      <input
                        className="input text-sm w-full"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveDisplayName(config);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                      />
                      <GhostButton
                        onClick={() => saveDisplayName(config)}
                        disabled={saving}
                        className="!text-blue-600 !no-underline !text-xs !px-2"
                      >
                        Save
                      </GhostButton>
                      <IconButton
                        onClick={() => setEditingId(null)}
                        aria-label="Cancel"
                        variant="ghost"
                        size="sm"
                        className="!text-zinc-400"
                      >
                        <X className="w-3.5 h-3.5" />
                      </IconButton>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800">
                        {config.displayName || config.portalName || "HubSpot"}
                      </span>
                      <IconButton
                        onClick={() => {
                          setEditingId(config.id);
                          setEditName(config.displayName);
                        }}
                        aria-label="Edit name"
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <EditIcon className="w-3.5 h-3.5" aria-hidden />
                      </IconButton>
                    </div>
                  )}
                  {config.authMethod === "oauth" ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
                        OAuth
                      </span>
                      {config.oauthConnectedAt && (
                        <span className="text-xs text-zinc-400">
                          Connected{" "}
                          {new Date(
                            config.oauthConnectedAt
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-zinc-400">
                        {config.accessTokenMasked}
                      </span>
                      <Link
                        to="/integrations/hubspot/create"
                        className="text-xs text-blue-500 hover:text-blue-700 underline"
                        title="Upgrade to OAuth for Marketplace compatibility"
                      >
                        Upgrade to OAuth →
                      </Link>
                    </div>
                  )}
                </td>
                <td className="py-3 pr-4 text-zinc-500 text-xs">
                  {config.hubId || "—"}
                </td>
                <td className="py-3 pr-4">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${config.pushTranscripts ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                  >
                    {config.pushTranscripts ? "Auto-push on" : "Disabled"}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <button
                    onClick={() => toggleEnabled(config)}
                    className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                  >
                    {config.enabled ? "Active" : "Disabled"}
                  </button>
                </td>
                <td className="py-3 text-right">
                  <GhostButton
                    onClick={() => setDeleteTarget(config)}
                    className="!no-underline !text-xs !text-red-500 hover:!text-red-700 !px-2 !py-1 !rounded border border-red-200 hover:border-red-400"
                  >
                    Remove
                  </GhostButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteTarget && (
        <Modal show={true}>
          <ModalBody>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 text-red-500" />
                <span className="font-semibold text-zinc-800">
                  Remove Account
                </span>
              </div>
              <IconButton
                onClick={() => setDeleteTarget(null)}
                aria-label="Close"
                variant="ghost"
              >
                <IconClose className="w-6 text-zinc-400" />
              </IconButton>
            </div>
            <p className="text-sm text-zinc-600 mb-6">
              Remove{" "}
              <strong>{deleteTarget.displayName || "this account"}</strong>?
              This cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <SecondaryButton
                onClick={() => setDeleteTarget(null)}
                className="!border !border-blue-500 !bg-transparent !text-blue-600"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={confirmDelete}
                className="!bg-red-600 hover:!bg-red-700"
              >
                Remove
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </SectionCard>
  );
}
